import React from 'react'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage, getSrc } from 'gatsby-plugin-image'
import Breadcrumb from '../components/Breadcrumb'
import CreateInvoiceCta from '../components/CreateInvoiceCta'
import SEO from '../components/SEO/SEO'
import TemplateIconBlock from '../components/TemplateIconBlock'
import Wysiwyg from '../components/Wysiwyg'
import Container from '../containers/Container'
import Layout from '../containers/Layout'
import * as style from '../sass/templates/Template.module.scss'

const TemplateTemplate = (props) => {
    const {
        data: {
            wpTemplate: {
                acfTemplateFiles,
                ancestors,
                cleanExcerpt,
                cleanTitle,
                content,
                date,
                featuredImage,
                modified,
                seo,
            },
        },
        path,
    } = props

    const imageAltText = featuredImage?.node?.altText || ''
    const imageTitle = featuredImage?.node?.title || ''

    const facebookImage = getSrc(featuredImage?.node?.facebook)
    const twitterImage = getSrc(featuredImage?.node?.twitter)
    const articleImage = getImage(featuredImage?.node?.image260)

    // Fallback if no ancestors nodes provided
    const breadcrumbs = ancestors?.nodes || []

    // Get downloads into array
    let fileDownloads = [
        { name: 'PDF', file: acfTemplateFiles.pdf?.localFile?.publicURL },
        {
            name: 'Excel',
            file: acfTemplateFiles.xls?.localFile?.publicURL,
        },
        {
            name: 'Word',
            file: acfTemplateFiles.word?.localFile?.publicURL,
        },
    ]

    fileDownloads = fileDownloads.filter((item) => item.file)

    // Get Links into array
    let fileLinks = [
        { name: 'Dropbox', file: acfTemplateFiles.dropbox },
        { name: 'OneDrive', file: acfTemplateFiles.onedrive },
        {
            name: 'GoogleDrive',
            file: acfTemplateFiles.googleDrive,
        },
    ]

    fileLinks = fileLinks.filter((item) => item.file)

    return (
        <Layout>
            <SEO
                dateModified={modified}
                datePublished={date}
                description={cleanExcerpt}
                facebookPostImage={facebookImage}
                postType="template"
                title={cleanTitle}
                twitterPostImage={twitterImage}
                url={path}
                yoastTitle={seo.title}
            />

            <Container>
                <main className={style.templateWrap} role="main">
                    <Breadcrumb
                        crumbs={[
                            ...breadcrumbs,
                            {
                                title: cleanTitle,
                                uri: path,
                            },
                        ]}
                    />
                    <h1 className={`${style.title} atf`}>{cleanTitle}</h1>
                    <div className={style.flex}>
                        <article className={style.article}>
                            <CreateInvoiceCta />
                            <Wysiwyg content={content} />
                        </article>

                        <aside className={style.aside}>
                            {articleImage && (
                                <div className={style.featuredImage}>
                                    <GatsbyImage
                                        alt={imageAltText}
                                        image={articleImage}
                                        loading="eager"
                                        title={imageTitle}
                                    />
                                </div>
                            )}
                            <div className={style.fileWrapper}>
                                <div>
                                    <TemplateIconBlock
                                        download
                                        items={fileDownloads}
                                        title="Download"
                                    />
                                </div>
                                <div>
                                    <TemplateIconBlock
                                        items={fileLinks}
                                        title="Open With"
                                    />
                                </div>
                            </div>
                        </aside>
                    </div>
                </main>
            </Container>
        </Layout>
    )
}

export default TemplateTemplate

export const pageQuery = graphql`
    query PostByID($id: String!) {
        site {
            siteMetadata {
                title
            }
        }
        wpTemplate(id: { eq: $id }) {
            title
            cleanTitle
            content
            excerpt
            cleanExcerpt
            status
            date
            modified
            featuredImage {
                node {
                    altText
                    title
                    ...FeaturedImage
                    ...Facebook
                    ...Twitter
                }
            }
            acfTemplateFiles {
                ...TemplateFiles
            }
            seo {
                metaDesc
                metaKeywords
                metaRobotsNofollow
                metaRobotsNoindex
                opengraphDescription
                opengraphTitle
                title
                twitterDescription
                twitterTitle
            }
            ancestors {
                nodes {
                    ... on WpTemplate {
                        title
                        uri
                    }
                }
            }
        }
    }
`
